import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, MapPinIcon, PhoneIcon, MailIcon, CheckCircleIcon, BookOpenIcon, BriefcaseIcon, ChevronDownIcon, ChevronLeftIcon, AwardIcon, BuildingIcon, LoaderIcon } from 'lucide-react';
import { getUniversityBySlug, getProgramBySlug, University, UniversityProgram } from '../data/universities';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { TCPAConsent } from '../components/TCPAConsent';
import { TrustSignals } from '../components/TrustSignals';
import { usePageMeta } from '../hooks/usePageMeta';
const startDateOptions = ['Immediately', 'Within 1 month', 'Within 3 months', 'Within 6 months', 'Within 1 year', 'Not sure yet'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 30; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
export function UniversityPage() {
  const {
    universitySlug,
    programSlug
  } = useParams<{
    universitySlug: string;
    programSlug?: string;
  }>();
  const navigate = useNavigate();
  const [university, setUniversity] = useState<University | null>(null);
  usePageMeta(university ? `${university.name} Online Programs | FindOnlineColleges` : 'College Programs | FindOnlineColleges', university ? `Explore online degree programs at ${university.name}. ${university.description?.slice(0, 120) || 'Compare programs and request program information.'}` : 'Explore online degree programs at accredited colleges and universities.', {
    ogImage: university?.logoUrl || 'https://images.unsplash.com/photo-1562774053-701939374585?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const [selectedProgram, setSelectedProgram] = useState<UniversityProgram | null>(null);
  const [formStep, setFormStep] = useState(0); // 0=program select, 1=education, 2=zip/address, 3=contact
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  useEffect(() => {
    if (universitySlug) {
      const foundUni = getUniversityBySlug(universitySlug);
      if (foundUni) {
        setUniversity(foundUni);
        if (programSlug) {
          const foundProg = getProgramBySlug(foundUni, programSlug);
          if (foundProg) {
            setSelectedProgram(foundProg);
            if (formStep === 0) setFormStep(1);
          } else {
            setSelectedProgram(null);
          }
        } else {
          setSelectedProgram(null);
          setFormStep(0);
        }
      } else {
        setUniversity(null);
      }
    }
    window.scrollTo(0, 0);
  }, [universitySlug, programSlug]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    if (newSlug) {
      navigate(`/colleges/${universitySlug}/${newSlug}`, {
        replace: true
      });
    } else {
      navigate(`/colleges/${universitySlug}`, {
        replace: true
      });
    }
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        // Fallback: let user enter manually
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    if (validateUniStep2()) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = () => {
    if (validateUniStep3()) {
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Lead submitted:', cleanedFormData);
      navigate('/thank-you', {
        state: {
          formData: cleanedFormData,
          partner: university ? {
            name: university.name,
            logo: university.logoUrl || '',
            primaryColor: '#4A3F8C',
            accentColor: '#5FA396',
            backUrl: `/colleges/${university.slug}`,
            programTitle: selectedProgram?.title || ''
          } : undefined
        }
      });
    }
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  if (!university) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-3153" data-mp="39l9raz6231go">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-3154" data-mp="34ngli78o09gx">
          University Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-3155" data-mp="30lv4qat0fb1g">
          We couldn't find the university you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-3156" data-mp="2vnbfo1j4dj4v">
          Return Home
        </Link>
      </div>;
  }
  const renderFormContent = () => {
    // Step 0: Just program selector (shown when no program selected)
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-3157" data-mp="2c1rzp4edhthj">
          <div data-id="element-3158" data-mp="2c2tcbdb1eo1d">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3159" data-mp="2gzjo3oft6xgz">
              Select a Program
            </label>
            <div className="relative" data-id="element-3160" data-mp="34ri4gmu47j0z">
              <select id="hero-program-select" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-navy focus:border-navy font-medium text-gray-800" data-id="element-3161" data-mp="39oibazysgpt5">
                <option value="" data-id="element-3162" data-mp="3no9v5hbzl0f2">-- Choose a Program --</option>
                {(() => {
                const groups: Record<string, typeof university.programs> = {};
                university.programs.forEach(p => {
                  const area = p.focusArea === '4' ? 'Computer Technology' : p.focusArea === '1' ? 'Business' : 'Other';
                  if (!groups[area]) groups[area] = [];
                  groups[area].push(p);
                });
                return Object.entries(groups).map(([area, progs]) => <optgroup key={area} label={area} data-id="element-3163" data-mp="1lpy3obgrglx4">
                      {progs.map(p => <option key={p.slug} value={p.slug} data-id="element-3164" data-mp="y0vl91eayp1k">
                          {p.title}
                        </option>)}
                    </optgroup>);
              })()}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500" data-id="element-3165" data-mp="171jznx7lyrez">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3166" data-mp="121z13nwcol2s" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-3167" data-mp="2ig798dgdiruw">
            Select a program above to get started
          </p>
        </div>;
    }
    // Step 1: Education level, start date, grad year
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-3168" data-mp="2xl1lfrm3dugw">
          {/* Program selector */}
          <div data-id="element-3169" data-mp="32i1umbe8ndc8">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3170" data-mp="32jwdzl9v2xe7">
              Program
            </label>
            <div className="relative" data-id="element-3171" data-mp="3bawsgk1dm6rz">
              <select id="step1-program" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-2.5 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-3172" data-mp="3bbye1ig0qbny">
                {(() => {
                const groups: Record<string, typeof university.programs> = {};
                university.programs.forEach(p => {
                  const area = p.focusArea === '4' ? 'Computer Technology' : p.focusArea === '1' ? 'Business' : 'Other';
                  if (!groups[area]) groups[area] = [];
                  groups[area].push(p);
                });
                return Object.entries(groups).map(([area, progs]) => <optgroup key={area} label={area} data-id="element-3173" data-mp="1sd8vcry7gnac">
                      {progs.map(p => <option key={p.slug} value={p.slug} data-id="element-3174" data-mp="1nl8pyjzd926d">
                          {p.title}
                        </option>)}
                    </optgroup>);
              })()}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400" data-id="element-3175" data-mp="1ellwqlgsj2oe">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3176" data-mp="2baa1ig3hwtfv" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-3177" data-mp="26djndyb947x3" />

          {/* Education Level */}
          <div data-id="element-3178" data-mp="229lonopu8z4k">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3179" data-mp="2293h3vxq6yhc">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-3180" data-mp="qw9g50j4vsc2">
              <option value="" data-id="element-3181" data-mp="3e5zv8gjkkssx">-- Please Select --</option>
              {educationLevels.map(level => <option key={level} value={level} data-id="element-3182" data-mp="3976g6jomwtqn">
                  {level}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-3183" data-mp="3mzcwq6vp92qm">
                {errors.currentEducation}
              </p>}
          </div>

          {/* Start Date */}
          <div data-id="element-3184" data-mp="p0j9w471fyn">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3185" data-mp="oh0yeamspk3">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-3186" data-mp="9pp0t0sspyga">
              <option value="" data-id="element-3187" data-mp="2bhtmhujpbzik">-- Please Select --</option>
              {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-3188" data-mp="27h1cv06x3slr">
                  {opt}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-3189" data-mp="1xe4cvwb6mkj7">{errors.startDate}</p>}
          </div>

          {/* Grad Year */}
          <div data-id="element-3190" data-mp="21dmxss306vm1">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3191" data-mp="21g96ww5jlj6d">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-3192" data-mp="1sk9ztbq313t3">
              <option value="" data-id="element-3193" data-mp="1iozuof4e0gb3">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-3194" data-mp="14op6xkydjhu5">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-3195" data-mp="19ns8uwj257xw">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3" data-id="element-3196" data-mp="2z3hvx14r5ad4">
            <button type="button" onClick={() => navigate(`/colleges/${universitySlug}`, {
            replace: true
          })} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3197" data-mp="2z0cabvvc0qm5">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3198" data-mp="2xndv1zhb8gge" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3199" data-mp="2snrhtq1hf07l">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 2: Zip code → city/state/address
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-3200" data-mp="3biy4m4b5fmb5">
          <div data-id="element-3201" data-mp="36lzahlrhjfcy">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3202" data-mp="36m7lt97ymcm1">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) {
              lookupZip(val);
            }
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-3203" data-mp="7ximm0qs0y2z" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-3204" data-mp="pd4lefnsoogz">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-3205" data-mp="tezrdxgo7q13">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-3206" data-mp="tcwx318hjaqk" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-3207" data-mp="135b160n1ctfw">
                <div data-id="element-3208" data-mp="184facr9ymr68">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3209" data-mp="182uk3duohue6">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="City" data-id="element-3210" data-mp="1h42ehku2qb2z" />
                </div>
                <div data-id="element-3211" data-mp="2nma980g1kta4">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3212" data-mp="2nkpfue5grgdu">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="State" data-id="element-3213" data-mp="1vu2sz14g7iw9" />
                </div>
              </div>

              <div data-id="element-3214" data-mp="1zv4u5nhxe50v">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3215" data-mp="1zumra80rcsjz">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal normal-case" data-id="element-3216" data-mp="3bbuuwr3ky4n3">
                    (optional)
                  </span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-3217" data-mp="3jugb5g07mr5k" />
              </div>
            </>}

          <div className="flex gap-3" data-id="element-3218" data-mp="3ev3nwu5ffi2g">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3219" data-mp="6d79l93pb964">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3220" data-mp="3ssrztchinevd" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3221" data-mp="1kw9o68ynndk">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 3: Contact info + TCPA
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-3222" data-mp="yznp1h815ct5">
          <div className="grid grid-cols-2 gap-3" data-id="element-3223" data-mp="z0qfjo1fxd8j">
            <div data-id="element-3224" data-mp="u0b2yyozg9rm">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3225" data-mp="tzsxrckcel7k">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-3226" data-mp="l0o34hbwjc0o" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-3227" data-mp="bbe9uy5bnnaz">{errors.firstName}</p>}
            </div>

            <div data-id="element-3228" data-mp="6cb57et97j3j">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3229" data-mp="6cuoayun4dj5">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-3230" data-mp="3tfpn9j4talqe" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-3231" data-mp="2dvypnpu005gj">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-3232" data-mp="2hzwp61n8jia4">
            <div data-id="element-3233" data-mp="2mxg6jaf8cb6a">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3234" data-mp="2mxya6s481ro8">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-3235" data-mp="1u90t2sbqy0x2" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-3236" data-mp="23ykfu92kyex7">{errors.email}</p>}
            </div>

            <div data-id="element-3237" data-mp="16jpzbustd6sf">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3238" data-mp="16k863ld3r3el">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-3239" data-mp="4v7arpjcq54h" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-3240" data-mp="dwye4nqf5zv6">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2" data-id="element-3241" data-mp="9t8vsg0c8eil">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-3242" data-mp="9wefffq76o6x">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-navy focus:ring-navy cursor-pointer flex-shrink-0" data-id="element-3243" data-mp="nsi7b2dlmkg8" />
              <span className="text-xs text-gray-600 leading-relaxed" data-id="element-3244" data-mp="skt6g30im27q">
                By submitting this form, you are giving your express written
                consent to be contacted regarding our services using email,
                telephone or text, including our use of automated technology for
                calls or texts to the number provided above. This consent is not
                required to purchase goods or services. and you may always call
                us directly.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-3245" data-mp="1c61so7h6pv7j">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3" data-id="element-3246" data-mp="1g6vgl0umna0f">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3247" data-mp="1ga18enun4pzu">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3248" data-mp="sj42pstfuox1" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} className="flex-1 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3249" data-mp="xh5o4kec70bo">
              Submit
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <main className="flex-grow bg-light pb-16" data-id="element-3250" data-mp="mncaaw5bjjmm">
      {/* College Header */}
      <header className="bg-white border-b border-gray-200" data-id="element-3251" data-mp="rngrkoe1widw">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between" data-id="element-3252" data-mp="rkub1z96msjj">
          <Link to={`/colleges/${universitySlug}`} className="flex items-center" data-id="element-3253" data-mp="imilm0k1y0so">
            <img src="https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp" alt={`${university.name} logo`} className="h-14 md:h-16 w-auto object-contain" data-id="element-3254" data-mp="iomoqga4mvrw" />
          </Link>
          <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-3255" data-mp="1afsqf15hx1ja">
            <span className="hidden sm:inline-flex items-center" data-id="element-3256" data-mp="wls2l2g30yxp">
              <PhoneIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-3257" data-mp="11kuz1yhzxwu2" />
              {university.phone}
            </span>
            <a href={`mailto:${university.email}`} className="hidden md:inline-flex items-center hover:text-navy transition-colors" data-id="element-3258" data-mp="1p9xkzijnybud">
              <MailIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-3259" data-mp="1u3s5upb08wv1" />
              {university.email}
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="hero-bg relative overflow-hidden" data-id="element-3260" data-mp="1zenhh8wasnr7">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-3261" data-mp="2d6u0d2qu58uc" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10 md:py-16" data-id="element-3262" data-mp="1ggtcr727cyem">
          <div className="inline-flex items-center text-gold font-semibold tracking-wider text-sm uppercase mb-6" data-id="element-3263" data-mp="1ugcad5xe0rco">
            <BuildingIcon className="w-4 h-4 mr-2" data-id="element-3264" data-mp="1pfog8bdv3ue8" />
            {university.name} &middot; {university.location}
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start" data-id="element-3265" data-mp="35t4ze8ysn1p1">
            {/* Left: Program Info */}
            <div className="lg:w-1/2 flex flex-col justify-center" data-id="element-3266" data-mp="38wktr1rt9fwk">
              <AnimatePresence mode="wait" data-id="element-3267" data-mp="38wb279r4b0or">
                <motion.div key={selectedProgram?.slug || 'overview'} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.25
              }} data-id="element-3268" data-mp="3cxwb6k0j6uto">
                  <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold text-white mb-5 leading-tight" data-id="element-3269" data-mp="2p6iigcygwx86">
                    {selectedProgram ? selectedProgram.title : `Explore Programs at ${university.name}`}
                  </h1>
                  <p className="text-base md:text-lg text-gray-200 leading-relaxed mb-6" data-id="element-3270" data-mp="33xrep4wvlgua">
                    {selectedProgram ? selectedProgram.description : university.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {!selectedProgram && <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium self-start" data-id="element-3271" data-mp="23xe5605z52c9">
                  <ChevronDownIcon className="w-4 h-4 mr-2 animate-bounce" data-id="element-3272" data-mp="1yzcqdrfkzp6l" />
                  Select a program to get started
                </div>}

              {selectedProgram && <div className="flex flex-wrap gap-3 text-sm text-gray-300" data-id="element-3273" data-mp="1ozdgnr7fdss5">
                  {selectedProgram.keyFacts.slice(0, 2).map((fact, idx) => <span key={idx} className="inline-flex items-center bg-white/10 border border-white/15 rounded-full px-3 py-1" data-id="element-3274" data-mp="1l0wgjrmf9ik8" data-mpi={idx}>
                      <CheckCircleIcon className="w-3.5 h-3.5 text-green-400 mr-1.5" data-id="element-3275" data-mp="1b8qpck3zv563" />
                      {fact}
                    </span>)}
                </div>}
            </div>

            {/* Right: Form Card */}
            <div className="lg:w-1/2 w-full flex justify-center lg:justify-end" data-id="element-3276" data-mp="11b2l93ki0lb9">
              <div className="w-full max-w-md" data-id="element-3277" data-mp="x9h7zh41y50j">
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-3278" data-mp="x9qzj94qwk8c">
                  <div className="bg-navy px-6 py-3.5" data-id="element-3279" data-mp="1m0n8cntmmmb5">
                    <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-3280" data-mp="1qz7y1tb95nlc">
                      Request Information
                    </h2>
                  </div>

                  {/* Progress Bar */}
                  {formStep > 0 && <div className="px-6 pt-4" data-id="element-3281" data-mp="1h726ulstra77">
                      <div className="flex justify-between items-center mb-1.5" data-id="element-3282" data-mp="13946ikbc9eap">
                        <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-3283" data-mp="18874xlwtcm9p">
                          Step {formStep} of {totalSteps}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-3284" data-mp="yaauyxfm3xd6">
                          {progressPercent}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-3285" data-mp="2oni0g9eyadcz">
                        <motion.div className="h-full bg-gold rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${progressPercent}%`
                    }} transition={{
                      duration: 0.4,
                      ease: 'easeOut'
                    }} data-id="element-3286" data-mp="2snirqun5ej1g" />
                      </div>
                    </div>}

                  <div className="p-6" data-id="element-3287" data-mp="lyi8078f53jb">
                    <AnimatePresence mode="wait" data-id="element-3288" data-mp="10oxr9gh9y1eu">
                      <motion.div key={formStep} initial={{
                      opacity: 0,
                      x: 30
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} exit={{
                      opacity: 0,
                      x: -30
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-3289" data-mp="10nmsjv2orjul">
                        {renderFormContent()}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                <p className="text-center text-gray-300 text-sm mt-3 italic" data-id="element-3290" data-mp="1afknzb3pquso">
                  Used by{' '}
                  <strong className="text-white font-semibold" data-id="element-3291" data-mp="1fexatvul41v9">
                    2M+ students
                  </strong>{' '}
                  to choose the right school.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TrustSignals data-id="element-3292" data-mp="1tdwxixyje6os" />

      {/* Program Content — Full Width */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-3293" data-mp="1tbj5kxz5lalg">
        <AnimatePresence mode="wait" data-id="element-3294" data-mp="1ybfar0jl782l">
          {selectedProgram ? <motion.div key={selectedProgram.slug} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="space-y-8" data-id="element-3295" data-mp="233ow039rbjyj">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3296" data-mp="2h2ybf0q52a1g">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-3297" data-mp="2h1555mjftrtz">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3298" data-mp="5di28ndurwkz">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3299" data-mp="iw07atnvgdk">
                      <BriefcaseIcon className="w-6 h-6 text-navy" data-id="element-3300" data-mp="3sldd66rhj8uo" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-3301" data-mp="3nmix3ckwjye9">
                      Potential Job Opportunities
                    </h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 font-medium" data-id="element-3302" data-mp="k50q17cyevew">
                    {selectedProgram.jobOpportunitiesIntro}:
                  </p>
                  <ul className="space-y-3" data-id="element-3303" data-mp="fcha43qsno6x">
                    {selectedProgram.jobOpportunities.map((job, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-3304" data-mp="aefszocxhyy3" data-mpi={idx}>
                        <span className="w-1.5 h-1.5 rounded-full bg-navy mt-1.5 mr-3 flex-shrink-0" data-id="element-3305" data-mp="17v4t9dm8ct5d" />
                        <span data-id="element-3306" data-mp="1321xqzxw79mj">{job}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-3307" data-mp="bequ7kaxd04t">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3308" data-mp="g8v0e1cd944u">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3309" data-mp="1jp4oncqlw1p">
                      <AwardIcon className="w-6 h-6 text-navy" data-id="element-3310" data-mp="6i0eiy3186jx" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-3311" data-mp="u69tbg1tvu1k">Key Facts</h2>
                  </div>
                  <ul className="space-y-4" data-id="element-3312" data-mp="z5w9nycydqel">
                    {selectedProgram.keyFacts.map((fact, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-3313" data-mp="l6ef12cz7ixb" data-mpi={idx}>
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" data-id="element-3314" data-mp="p76p1xto9run" />
                        <span className="leading-tight pt-0.5" data-id="element-3315" data-mp="35wc38xalfrwg">{fact}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-3316" data-mp="39vkxs4dgjtt2">
                <div className="bg-navy px-6 py-4 flex items-center" data-id="element-3317" data-mp="32g99h2b3awqj">
                  <BookOpenIcon className="w-5 h-5 text-gold mr-3" data-id="element-3318" data-mp="2xea6vun6c3un" />
                  <h2 className="text-lg font-bold text-white" data-id="element-3319" data-mp="3ccvtxpzi7ls1">
                    Program Curriculum
                  </h2>
                </div>
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3320" data-mp="37e107t000ct5">
                  <thead className="bg-gray-50" data-id="element-3321" data-mp="2joq62ud5lgnz">
                    <tr data-id="element-3322" data-mp="2jlc961n9dznx">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3323" data-mp="2ep3tmn55g4g8">
                        Course Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3324" data-mp="2on1fx53g7r67">
                        Credits
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100" data-id="element-3325" data-mp="1vxb3d0hgsxc7">
                    {selectedProgram.curriculum.map((item, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-3326" data-mp="vfwlth6c9w9b" data-mpi={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3327" data-mp="qmk8423779vc">
                          {item.course}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-3328" data-mp="gqqmzubey81y">
                          {item.credits}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </motion.div> : <motion.div key="default-overview" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="space-y-8" data-id="element-3329" data-mp="14eqn3kum7at7">
              {/* Benefits of Online Learning */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-3330" data-mp="3p5r4xvgxw0by">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3331" data-mp="3k64sho8xqo47">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3332" data-mp="1zovn47vubynx">
                    <CheckCircleIcon className="w-6 h-6 text-navy" data-id="element-3333" data-mp="1zmakvhrmrgpn" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-3334" data-mp="24ldoqyvvjgw1">
                    Benefits of Online Learning with Global Tech College
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3335" data-mp="2eeui61s3mtz6">
                  {[{
                icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-3336" data-mp="2jc2yrxxlmpyb" />,
                title: 'High-Quality Education Experience'
              }, {
                icon: <BookOpenIcon className="w-6 h-6 text-crimson" data-id="element-3337" data-mp="2scj5dg9n2d7e" />,
                title: 'Flexible Online Schedules'
              }, {
                icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-3338" data-mp="2xblzw6rzt114" />,
                title: 'Accredited by ACCET'
              }, {
                icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-3339" data-mp="12y316tm87ce8" />,
                title: 'Career Services Assistance'
              }, {
                icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-3340" data-mp="uy6ltfhb2zxu" />,
                title: '1-1 Student Consulting'
              }, {
                icon: <BuildingIcon className="w-6 h-6 text-crimson" data-id="element-3341" data-mp="qy63hjr364li" />,
                title: 'Very Low Tuition Fees'
              }].map((item, idx) => <div key={idx} className="bg-gray-50 rounded-xl border border-gray-100 p-5 flex items-center gap-4" data-id="element-3342" data-mp="zw8dupwpm9lg" data-mpi={idx}>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-sm flex-shrink-0" data-id="element-3343" data-mp="1duy9s27sm388">
                        {item.icon}
                      </div>
                      <h3 className="font-bold text-navy text-sm leading-tight" data-id="element-3344" data-mp="1nkrdkh1p1e1x">
                        {item.title}
                      </h3>
                    </div>)}
                </div>
              </div>

              {/* Highlights Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3345" data-mp="1sl5nk4muuy2l">
                {[{
              icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-3346" data-mp="2c78l2hdcba3f" />,
              title: 'Title IV Approved',
              desc: 'Federal financial aid available for eligible students'
            }, {
              icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-3347" data-mp="229av9pfu158p" />,
              title: 'VA Benefits',
              desc: 'Approved for veterans and dependents education benefits'
            }, {
              icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-3348" data-mp="1sigayqm5cfpw" />,
              title: 'ACCET Accredited',
              desc: 'Accrediting Council for Continuing Education & Training'
            }, {
              icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-3349" data-mp="1jj2zjsegph2j" />,
              title: 'Financial Aid',
              desc: 'Available for students who qualify'
            }].map((item, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-center" data-id="element-3350" data-mp="19kdlkaposgrk" data-mpi={idx}>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-crimson/5 mb-3" data-id="element-3351" data-mp="1ejgjzcb5voqk">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-navy text-sm mb-1" data-id="element-3352" data-mp="2cv9pyvx68d19">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-3353" data-mp="2839j0jioojv6">{item.desc}</p>
                  </div>)}
              </div>

              {/* Available Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-3354" data-mp="1y6567aaqa0r1">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3355" data-mp="1u4jj6w4hc634">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3356" data-mp="1u6wba23ybwtw">
                    <BookOpenIcon className="w-6 h-6 text-navy" data-id="element-3357" data-mp="1p68dmxl7whqp" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-3358" data-mp="1k7dqxkk4q91b">
                    Available Programs
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3359" data-mp="3jh3tmkyqez3i">
                  {university.programs.map(prog => <button key={prog.slug} onClick={() => navigate(`/colleges/${universitySlug}/${prog.slug}`, {
                replace: true
              })} className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-navy/30 hover:bg-navy/[0.02] transition-all text-left group" data-id="element-3360" data-mp="3m3w5ite5f72t">
                      <div className="p-1.5 bg-navy/5 rounded-md group-hover:bg-navy/10 transition-colors mt-0.5" data-id="element-3361" data-mp="4n4s0g7sqoco">
                        <GraduationCapIcon className="w-4 h-4 text-navy" data-id="element-3362" data-mp="335tl6rwjpybc" />
                      </div>
                      <div data-id="element-3363" data-mp="383bmbw4bn98s">
                        <h3 className="font-semibold text-navy text-sm group-hover:text-navy-light transition-colors" data-id="element-3364" data-mp="384mz2lfqvrc1">
                          {prog.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-3365" data-mp="3i32h7ywsnq9e">
                          {prog.degreeLevel === '1' ? 'Certificate' : 'Associate Degree'}{' '}
                          · {prog.keyFacts[0]}
                        </p>
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Accreditation */}
              <div className="bg-navy/5 rounded-xl border border-navy/10 p-6 flex items-start gap-4" data-id="element-3366" data-mp="2ogn1ctjs1n3k">
                <div className="p-2 bg-white rounded-lg shadow-sm" data-id="element-3367" data-mp="32gemrfcmi5rl">
                  <AwardIcon className="w-6 h-6 text-navy" data-id="element-3368" data-mp="32dk2bntm0ky6" />
                </div>
                <div data-id="element-3369" data-mp="2yf2x59tr1kje">
                  <h3 className="font-bold text-navy mb-1" data-id="element-3370" data-mp="2aoqdx2t201di">Accreditation</h3>
                  <p className="text-sm text-gray-600" data-id="element-3371" data-mp="2ammbkpaszafc">
                    The Accrediting Council for Continuing Education & Training
                    (ACCET)
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-3372" data-mp="2jilmylxaqbyf">
                    {university.location}
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </main>;
}